import React, { useState, useMemo } from 'react';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { Text, Stack, Button } from '@moda/om';
import { VariantCellAddToBagFragment } from '../../../../../generated/types/VariantCellAddToBagFragment';
import { compact } from '../../../../../lib/compact';
import { getActiveVariantBy } from '../../../../../lib/getActiveVariantBy';
import { useAddToBag, ADD_TO_BAG_FRAGMENT } from '../../../../../hooks/useAddToBag';
import { VariantCellSizeSlider, VARIANT_CELL_SIZE_SLIDER_FRAGMENT } from '../VariantCellSizeSlider';
import './VariantCellAddToBag.scss';

export const VARIANT_CELL_ADD_TO_BAG_FRAGMENT = gql`
  fragment VariantCellAddToBagFragment on Variant {
    ...VariantCellSizeSliderFragment
    ...AddToBagFragment
  }
  ${VARIANT_CELL_SIZE_SLIDER_FRAGMENT}
  ${ADD_TO_BAG_FRAGMENT}
`;

type Props = {
  className?: string;
  variant: VariantCellAddToBagFragment;
  activeVariantId: string;
  position?: number;
  href: string;
};

export const VariantCellAddToBag: React.FC<Props> = ({
  className,
  variant,
  activeVariantId,
  position = 1,
  href
}) => {
  const [selectedSize, setSelectedSize] = useState<string>();

  const activeVariant = useMemo(() => getActiveVariantBy(variant, 'id', activeVariantId), [
    activeVariantId,
    variant
  ]);

  const selectedInventory = useMemo(
    () => compact(activeVariant.inventory).find(({ size }) => size === selectedSize),
    [activeVariant.inventory, selectedSize]
  );

  const { text, handleAddToBag, isOneSizedItem, disabled } = useAddToBag({
    activeVariantId,
    variant,
    position,
    size:
      activeVariant.inventory.length === 1 && activeVariant.inventory[0]?.size === 'OS'
        ? activeVariant.inventory[0]
        : selectedInventory,
    href,
    validatesSizePresenceOn: 'submit'
  });

  return (
    <Stack
      className={classNames('VariantCellAddToBag', className)}
      space={0}
      justifyContent="center"
      alignItems="center"
    >
      {isOneSizedItem ? (
        <Button
          className="VariantCellAddToBag__button"
          secondary
          disabled={disabled}
          onClick={() => handleAddToBag()}
        >
          {text}
        </Button>
      ) : (
        <>
          <Text treatment="body2">{text}</Text>
          <VariantCellSizeSlider
            variant={variant}
            activeVariantId={activeVariantId}
            onSelectSize={setSelectedSize}
            onClick={() => handleAddToBag()}
          />
        </>
      )}
    </Stack>
  );
};
